import { CachingContainer } from "@/lib/caching/container"
import { getCachingContext } from "@/lib/caching/context"
import type { Time } from "@/lib/time"
import type { Validation } from "@/lib/validation"

export namespace Caching {
  export type BaseStore = Record<string, unknown>

  export type CacheArgs<
    Store extends BaseStore,
    CacheKeys extends Keys<Store>,
    Data
  > = {
    attemptSalvage?: Validation.Functions.AttemptSalvage<`item`>
    allowSalvage?: boolean
    caller: string
    fetch: Functions.Fetch<Data>
    fetchName: string
    keys: CacheKeys
    metadata?: Partial<Metadata>
    name?: string
    refresh?: boolean
    validator: Validation.Validator<Data>
  }

  export type ContextData<Store extends BaseStore> = {
    cache: Functions.Cache<Store>
    processing: string | undefined
    store: Store
  }

  export type Metadata = {
    cachedAt: Timestamp
    expiresAt?: Timestamp
    expiry?: Time.Milliseconds // The maximum allowable number of milliseconds between cachedAt and when it should be considered to have expired
  }

  // Keys cannot be numeric strings, eg '1', because they cause JavaScript to see the record as an Array
  export type Key = string

  export type Keys<Store extends BaseStore> = [keyof Store, ...Key[]]

  export type Payload<Data> = {
    caching: Metadata
    data: Data
  }

  export type Timestamp = Time.EpochMilliseconds

  export const Container = CachingContainer

  export const getContext = getCachingContext

  export namespace Functions {
    export type BuildStore<Store extends BaseStore> = () => Store

    export type Cache<Store extends BaseStore> = <
      CacheKeys extends Keys<Store>,
      Data
    >(
      args: CacheArgs<Store, CacheKeys, Data> & {
        onValidationFailure: OnValidationFailure<Store, CacheKeys, Data>
      }
    ) => Promise<Payload<Data> | undefined>

    export type Fetch<Data> = () => Promise<Data>

    export type OnValidationFailure<
      Store extends BaseStore,
      CacheKeys extends Keys<Store>,
      Data
    > = (args: {
      cacheArgs: CacheArgs<Store, CacheKeys, Data>
      result: Validation.Result<Data>
    }) => Promise<void> | void
  }
}
